import {
  Component, ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output, QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { NuveiService } from "../../../services/nuvei.service";
import { BusinessService } from "../../../services/business.service";
import { DocumentType } from "../../../models/nuvei-application.model";
import { take } from "rxjs/operators";
import { snakeCaseToCamel } from "../../../../../../common/src/lib/services";
import { SettingsTabKey } from "../../../pages/profile/profile.component";
import { Router } from "@angular/router";
import { smoothScrollIntoView } from "../nuvei-application-step";
import { DOCUMENT } from "@angular/common";
import { NavigationService } from '../../../services/navigation.service';

export interface UploadedFile {
  name: string,
  mimeType: string,
  size: number,
  ts: Date,
  downloadUrl?: string,
}

export function fileExt(mimeType: string) {
  const mimeTypes: Record<string, string> = {
    "application/pdf": "PDF",
    "image/jpeg": "JPG",
    "image/png": "PNG",
    "text/plain": "TXT",
    "application/msword": "DOC",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  };

  const fileType = mimeTypes[mimeType];
  return fileType || "";
}

@Component({
  selector: 'app-nuvei-application-documents',
  templateUrl: './nuvei-application-documents.component.html',
  styleUrls: ['./nuvei-application-documents.component.scss']
})
export class NuveiApplicationDocumentsComponent {

  fileExt = fileExt;

  @ViewChildren('itemDiv') itemDivs!: QueryList<ElementRef>;
  @ViewChild('containerDiv') containerDiv!: ElementRef;

  @Input() uploadedFiles: {
    [T in DocumentType]?: UploadedFile
  } = {};
  @Input() missingDocuments!: DocumentType[];
  @Input() progress!: number;

  @Output() onFilesUpload = new EventEmitter<typeof this.uploadedFiles>();
  @Output() nextClick = new EventEmitter<boolean>();

  bgStyles = {
    top: '0px',
    left: '0px',
    width: '0px',
    height: '0px',
    opacity: '0'
  };
  activeIndex: number | null = null;

  constructor(
      @Inject(DOCUMENT) protected document: Document,
      private businessService: BusinessService,
      private nuveiService: NuveiService,
      private router: Router,
      private navigationService: NavigationService,
  ) {}

  focusedFields: { [field: string]: boolean } = {};
  fieldExplanations = [
    { id: 'proofOfBusiness', title: 'Proof of Business', explanation: 'A document proving your business is legally registered and operational.\nProvided by your local government, state, or federal business registry. Ensure it matches the business name and details you enter in the Legal Business Information form.' },
    { id: 'voidCheck', title: 'Void Check', explanation: 'An empty check from your business bank account with "VOID" written across it.\nRequest from your bank or print from an online banking portal.\nEnsure it has the correct business name and account details.' },
    { id: 'previousPaymentProcessor', title: 'Previous Payment Processor (Optional)', explanation: 'A document or statement from your last credit card payment processor.\nContact your previous processor or check your past billing statements.\nYou can often get the "Previous Payment Processor" details directly from the payment processor\'s website, usually in your account dashboard or downloadable statements.' },
  ];

  uploadingFiles: { [T in DocumentType]: boolean } = {
    proofOfBusiness: false,
    voidCheck: false,
    previousPaymentProcessor: false,
  };

  errors: { [T in DocumentType]?: UploadedFile & { message: string } } = {};

  missingDocumentErrorForId = {
    proofOfBusiness: 'Proof of business is required',
    voidCheck: 'Void check is required',
    previousPaymentProcessor: 'Previous payment processor is required'
  };

  activeExplanationChange(fieldId: string, focused: boolean) {
    this.focusedFields[fieldId] = focused;
    this.updateFocusedExplanation();
  }

  updateFocusedExplanation() {
    const activeFieldId = Object.keys(this.focusedFields).find(fieldId => this.focusedFields[fieldId]);

    if (!activeFieldId) {
      this.activeIndex = null;
      this.bgStyles = { ...this.bgStyles, opacity: '0' };
      return;
    }

    this.activeIndex = this.fieldExplanations.findIndex(explanation => explanation.id === activeFieldId);
    smoothScrollIntoView(this.document.getElementById('nuvei_app_' + activeFieldId)!, this.containerDiv.nativeElement);

    const activeElement = this.itemDivs.toArray()[this.activeIndex].nativeElement;
    const containerRect = this.containerDiv.nativeElement.getBoundingClientRect();
    const rect = activeElement.getBoundingClientRect();
    const containerScrollTop = this.containerDiv.nativeElement.scrollTop;

    this.bgStyles = {
      top: `${rect.top - containerRect.top + containerScrollTop - 8}px`,
      left: `${rect.left - containerRect.left - 8}px`,
      width: `${rect.width + 8}px`,
      height: `${rect.height + 16}px`,
      opacity: '1'
    };
  }

  fileSize(sizeInBytes: number) {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(2)} KB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
    }
  }

  async addFile(documentType: DocumentType, file: File) {
    if(!file)
      return;
    console.log(documentType, this.missingDocuments);
    const index = this.missingDocuments.indexOf(snakeCaseToCamel(documentType) as any);
    if(index !== -1)
      this.missingDocuments.splice(index, 1);
    console.log(index, this.missingDocuments);
    delete this.errors[documentType];
    const newValue = {
      name: file.name,
      mimeType: file.type,
      size: file.size,
      ts: new Date
    };

    if(file.size > 10000000){
      this.setError(documentType, newValue, 'File size is limited to 10MB');
      return;
    }
    if(!['application/pdf', 'image/jpeg', 'image/png', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)){
      this.setError(documentType, newValue, 'File type is not supported');
      return;
    }

    const prevValue = this.uploadedFiles[documentType];
    this.uploadingFiles[documentType] = true;
    try {
      this.uploadedFiles[documentType] = newValue;
      const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
      const { data, error } = await this.nuveiService.uploadFile(business.businessId, documentType, file);
      if(error) {
        this.setError(documentType, newValue, error);
      }
      this.uploadingFiles[documentType] = false;
      this.uploadedFiles[documentType]!.downloadUrl = URL.createObjectURL(file);
    } catch (e) {
      this.uploadedFiles[documentType] = prevValue;
    }
    this.onFilesUpload.emit(this.uploadedFiles);

  }

  async setError(documentType: DocumentType, data: UploadedFile, error: any) {
    let message = typeof error === 'string' ? error : await error.context.text();
    if(message.includes('mime type') && message.includes('is not supported'))
      message = 'File type is not supported';
    if(message.includes('The object exceeded the maximum allowed size'))
      message = 'File size is limited to 10MB';
    this.errors[documentType] = { ...data, message };
  }

  async removeFile(documentType: DocumentType) {
    if(this.errors[documentType]) {
      delete this.errors[documentType];
      return;
    }

    const prevValue = this.uploadedFiles[documentType];
    delete this.errors[documentType];
    try {
      delete this.uploadedFiles[documentType];
      const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
      await this.nuveiService.deleteFile(business.businessId, documentType);
      this.onFilesUpload.emit(this.uploadedFiles);
    } catch (e) {
      this.uploadedFiles[documentType] = prevValue;
    }
  }

  navigateToTransferOwnership() {
    localStorage.setItem(SettingsTabKey, 'Users');
    this.navigationService.setBackRoute('/payment-application');
    this.router.navigate(['/settings']);
  }

}